import { useMemo, useSyncExternalStore } from 'react';

import { getOnboardingDraftKey } from '~modules/onboarding/onboarding-draft-storage';
import {
  LessorOnboardingDraft,
  LessorOnboardingScreen,
  LessorOnboardingValues,
} from '~modules/onboarding/types';

const DRAFT_CHANGED_EVENT = 'rentify:lessor-onboarding-draft-changed';

const WITH_TENANT_PRE_SUBMIT_SCREENS: LessorOnboardingScreen[] = [
  'start',
  'property',
  'lease',
  'tenant',
  'review',
];

const PROPERTY_ONLY_SCREENS: LessorOnboardingScreen[] = [
  'start',
  'property',
  'property_only_phone',
  'property_only_complete',
];

const ONBOARDING_SCREENS: LessorOnboardingScreen[] = [
  ...WITH_TENANT_PRE_SUBMIT_SCREENS,
  'reminders_ready',
  'phone',
  'invite_ready',
  'property_only_phone',
  'property_only_complete',
];

export const ONBOARDING_DEFAULT_VALUES: LessorOnboardingValues = {
  property: {
    city: '',
    street: '',
    building: '',
    apartment: '',
    room_count: null,
    area: null,
  },
  lease: {
    rent_price: '',
    rent_due: 10,
    meter_reading_enabled: true,
    meter_reading_due: 25,
  },
  tenant: {
    firstname: '',
    lastname: '',
    patronymic: '',
    email: '',
    phone: '',
  },
};

export const createOnboardingValues = (): LessorOnboardingValues => ({
  property: { ...ONBOARDING_DEFAULT_VALUES.property },
  lease: { ...ONBOARDING_DEFAULT_VALUES.lease },
  tenant: { ...ONBOARDING_DEFAULT_VALUES.tenant },
});

export const createOnboardingDraft = (): LessorOnboardingDraft => ({
  flow: null,
  screen: 'start',
  values: createOnboardingValues(),
});

export const isOnboardingContinuationDraft = (
  draft?: LessorOnboardingDraft | null,
) =>
  Boolean(
    (draft?.flow === 'with_tenant' && draft.lease_id) ||
      (draft?.flow === 'property_only' && draft.property_id),
  );

export const clearSubmittedOnboardingValues = (
  draft: LessorOnboardingDraft,
  options: { keepLease?: boolean } = {},
): LessorOnboardingDraft => {
  const values = createOnboardingValues();

  return {
    ...draft,
    values: {
      ...values,
      ...(options.keepLease ? { lease: { ...draft.values.lease } } : {}),
    },
  };
};

const parseOnboardingDraft = (rawValue: string | null) => {
  if (!rawValue) return null;

  try {
    return JSON.parse(rawValue) as LessorOnboardingDraft;
  } catch {
    return null;
  }
};

const getOnboardingDraftSnapshot = (userId?: number) => {
  if (!userId || typeof localStorage === 'undefined') return null;

  return localStorage.getItem(getOnboardingDraftKey(userId));
};

const emitOnboardingDraftChanged = (userId: number) => {
  if (typeof window === 'undefined') return;

  window.dispatchEvent(
    new CustomEvent(DRAFT_CHANGED_EVENT, { detail: { userId } }),
  );
};

export const readOnboardingDraft = (userId?: number) => {
  if (!userId || typeof localStorage === 'undefined') return null;

  try {
    return parseOnboardingDraft(
      localStorage.getItem(getOnboardingDraftKey(userId)),
    );
  } catch {
    return null;
  }
};

export const writeOnboardingDraft = (
  userId: number,
  draft: LessorOnboardingDraft,
) => {
  try {
    localStorage.setItem(getOnboardingDraftKey(userId), JSON.stringify(draft));
  } catch {
    // Draft persistence is best-effort. The current session remains usable.
  }

  emitOnboardingDraftChanged(userId);
};

export const clearOnboardingDraft = (userId?: number) => {
  if (!userId) return;

  try {
    localStorage.removeItem(getOnboardingDraftKey(userId));
  } catch {
    // Ignore storage errors.
  }

  emitOnboardingDraftChanged(userId);
};

export const clearOnboardingDraftAfterCompletion = (
  userId: number,
  draft?: LessorOnboardingDraft | null,
) => {
  if (!isOnboardingContinuationDraft(draft)) {
    clearOnboardingDraft(userId);
  }
};

export const clearPropertyOnlyOnboardingDraft = (
  userId?: number,
  propertyId?: number | null,
) => {
  if (!userId || !propertyId) return;

  const draft = readOnboardingDraft(userId);

  if (draft?.flow === 'property_only' && draft.property_id === propertyId) {
    clearOnboardingDraft(userId);
  }
};

const subscribeToOnboardingDraft = (
  userId: number | undefined,
  onStoreChange: () => void,
) => {
  if (!userId || typeof window === 'undefined') return () => undefined;

  const handleDraftChanged = (event: Event) => {
    const changedUserId = (event as CustomEvent<{ userId?: number }>).detail
      ?.userId;

    if (changedUserId === userId) onStoreChange();
  };

  const handleStorage = (event: StorageEvent) => {
    if (event.key === getOnboardingDraftKey(userId)) onStoreChange();
  };

  window.addEventListener(DRAFT_CHANGED_EVENT, handleDraftChanged);
  window.addEventListener('storage', handleStorage);

  return () => {
    window.removeEventListener(DRAFT_CHANGED_EVENT, handleDraftChanged);
    window.removeEventListener('storage', handleStorage);
  };
};

export const useOnboardingDraft = (userId?: number) => {
  const rawDraft = useSyncExternalStore(
    (onStoreChange) => subscribeToOnboardingDraft(userId, onStoreChange),
    () => getOnboardingDraftSnapshot(userId),
    () => null,
  );

  return useMemo(() => parseOnboardingDraft(rawDraft), [rawDraft]);
};

const isOnboardingScreen = (
  screen?: string | null,
): screen is LessorOnboardingScreen =>
  Boolean(
    screen && ONBOARDING_SCREENS.includes(screen as LessorOnboardingScreen),
  );

const canRestoreReachedScreen = (
  screens: LessorOnboardingScreen[],
  currentScreen: LessorOnboardingScreen,
  requestedScreen: LessorOnboardingScreen,
) => {
  const currentIndex = screens.indexOf(currentScreen);
  const requestedIndex = screens.indexOf(requestedScreen);

  return requestedIndex >= 0 && requestedIndex <= currentIndex;
};

export const getRestoredOnboardingScreen = (
  draft: LessorOnboardingDraft,
  requestedScreen?: string | null,
  options: { isPhoneVerified?: boolean } = {},
): LessorOnboardingScreen => {
  if (draft.lease_id && draft.screen === 'phone' && options.isPhoneVerified) {
    return 'invite_ready';
  }

  if (
    draft.flow === 'property_only' &&
    draft.screen === 'property_only_phone' &&
    options.isPhoneVerified
  ) {
    return 'property_only_complete';
  }

  if (!isOnboardingScreen(requestedScreen)) return draft.screen;

  if (draft.flow === 'property_only') {
    if (draft.property_id) {
      if (requestedScreen === 'property_only_phone') {
        return options.isPhoneVerified
          ? 'property_only_complete'
          : 'property_only_phone';
      }

      if (requestedScreen === 'property_only_complete') {
        return 'property_only_complete';
      }

      return draft.screen;
    }

    if (requestedScreen === 'property_only_complete' && !draft.property_id) {
      return draft.screen;
    }

    return canRestoreReachedScreen(
      PROPERTY_ONLY_SCREENS,
      draft.screen,
      requestedScreen,
    )
      ? requestedScreen
      : draft.screen;
  }

  if (draft.lease_id) {
    if (requestedScreen === 'phone') {
      return options.isPhoneVerified ? 'invite_ready' : 'phone';
    }

    if (requestedScreen === 'invite_ready') {
      return options.isPhoneVerified ? 'invite_ready' : draft.screen;
    }

    if (
      requestedScreen === 'reminders_ready' &&
      draft.screen === 'reminders_ready'
    ) {
      return requestedScreen;
    }

    return draft.screen;
  }

  return canRestoreReachedScreen(
    WITH_TENANT_PRE_SUBMIT_SCREENS,
    draft.screen,
    requestedScreen,
  )
    ? requestedScreen
    : draft.screen;
};
